// Shared task building blocks (11-00 §2): StatusChip, DueLabel, LinkButton, PersonChip + undo-able actions.
import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, FolderOpen, Presentation, Link2 } from "lucide-react";
import { workspaceApi, tickTask, dueInfo, STATUS_TONE, emitTasksChanged } from "../_shared";

export const LINK_ICON = { gsheet: FileSpreadsheet, gdoc: FileText, gdrive: FolderOpen, gslides: Presentation, web: Link2 };
const LINK_TONE = { gsheet: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", gdoc: "text-sky-300 border-sky-500/40 bg-sky-500/10",
  gslides: "text-amber-300 border-amber-500/40 bg-amber-500/10", gdrive: "text-yellow-200 border-yellow-500/40 bg-yellow-500/10", web: "text-slate-300 border-white/15 bg-white/5" };

export function LinkButton({ link, label = false, testid }) {
  if (!link) return <span className="text-slate-600 text-[11px]">—</span>;
  const Icon = LINK_ICON[link.kind] || Link2;
  return (
    <a href={link.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title={link.label || link.url}
      className={`inline-flex items-center gap-1.5 rounded-md border px-1.5 py-1 text-[11px] hover:brightness-125 transition ${LINK_TONE[link.kind] || LINK_TONE.web}`}
      data-testid={testid}>
      <Icon className="w-3.5 h-3.5" />{label && <span>{link.label || ({ gsheet: "Open sheet", gdoc: "Open doc", gdrive: "Open folder", gslides: "Open slides" }[link.kind] || "Open link")}</span>}
    </a>
  );
}

export function DueLabel({ task, testid }) {
  const d = dueInfo(task);
  const tone = { red: "text-rose-300", amber: "text-amber-300", muted: "text-slate-500", normal: "text-slate-200" }[d.tone];
  return (
    <div className="leading-tight" data-testid={testid}>
      <div className={`text-[12px] font-medium ${tone}`}>{d.text}</div>
      {d.sub && <div className="text-[10.5px] text-slate-500 font-mono">{d.sub}</div>}
    </div>
  );
}

export function PersonChip({ name, pool, onClaim, testid }) {
  if (pool) return (
    <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-400" data-testid={testid}>
      Pool · <button onClick={(e) => { e.stopPropagation(); onClaim?.(); }} className="text-amber-300 hover:text-amber-200 font-semibold" data-testid={`${testid}-claim`}>Claim</button>
    </span>
  );
  if (!name) return <span className="text-slate-600 text-[12px]">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0" data-testid={testid}>
      <span className="w-5 h-5 shrink-0 rounded-full bg-amber-400/90 text-slate-900 text-[10px] font-bold grid place-items-center">{name.slice(0, 1).toUpperCase()}</span>
      <span className="text-[12.5px] text-slate-200 whitespace-nowrap">{name.split(" ")[0]}</span>
    </span>
  );
}

export function StatusChip({ task, statuses = [], onChange, disabled, testid }) {
  const [open, setOpen] = useState(false);
  const st = statuses.find((s) => s.key === task.status);
  const tone = STATUS_TONE[st?.color] || STATUS_TONE.slate;
  return (
    <span className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button disabled={disabled} onClick={() => setOpen((o) => !o)} data-testid={testid}
        className={`px-2 py-0.5 rounded-md border text-[11.5px] whitespace-nowrap ${tone.bg} ${tone.text} ${tone.border} ${disabled ? "cursor-default" : "hover:brightness-125"}`}>
        {st?.label || task.status}
      </button>
      {open && (
        <div className="absolute z-30 mt-1 left-0 min-w-[140px] rounded-lg border border-white/10 bg-slate-900 shadow-xl p-1" data-testid={`${testid}-menu`}>
          {statuses.filter((s) => s.key !== "cancelled" || task.status === "cancelled").map((s) => (
            <button key={s.key} onClick={() => { setOpen(false); if (s.key !== task.status) onChange?.(s.key); }}
              className="block w-full text-left px-2 py-1 rounded text-[12px] text-slate-200 hover:bg-white/10" data-testid={`${testid}-opt-${s.key}`}>{s.label}</button>
          ))}
        </div>
      )}
    </span>
  );
}

// Complete with a 5-second Undo (user request: undo accidental ticks on Dashboard + Workspace).
export async function tickRoutine(task, onChanged, onUndone) {
  let r;
  try { r = await workspaceApi.routineTick(task.id); }
  catch (e) { toast(e?.response?.data?.detail || "Couldn't tick", { description: task.title }); return false; }
  onChanged?.(); emitTasksChanged();
  toast.success(`Ticked · ${task.title}`, {
    duration: 12000, description: r.covered > 1 ? `Caught up ${r.covered} slots — the team can see you did it.` : "The team can see you did it.",
    action: { label: "Undo", onClick: async () => { try { await workspaceApi.routineUndo(r.tick_id); onUndone?.(); onChanged?.(); emitTasksChanged(); toast("Tick undone"); } catch { toast.error("Undo failed"); } } },
  });
  return true;
}

export const isRoutine = (t) => t?.recurrence?.mode === "routine" && (t.recurrence.rule || "none") !== "none";

export async function completeWithUndo(task, onChanged, onUndone) {
  if (isRoutine(task)) return tickRoutine(task, onChanged, onUndone);
  const each = task.team_id && task.assign_mode === "each";
  try {
    if (each) await tickTask(task.id, true);
    else await workspaceApi.patchTask(task.id, { status: "done" });
  } catch (e) {
    toast.error("Couldn't complete", { description: e?.response?.data?.detail || "" });
    return false;
  }
  onChanged?.(); emitTasksChanged();
  toast.success(`Completed · ${task.ref || ""} ${task.title}`.trim(), {
    duration: 15000, description: "Undo here, or Reopen it any time from Recently done.",
    action: { label: "Undo", onClick: async () => {
      try {
        if (each) await tickTask(task.id, false); else await workspaceApi.undoComplete(task.id);
        toast("Restored", { description: task.title }); onUndone?.(); onChanged?.(); emitTasksChanged();
      } catch { toast.error("Undo failed"); }
    } },
  });
  return true;
}

export async function snoozeDue(task, onChanged) {
  const base = task.due_date ? new Date(`${String(task.due_date).slice(0, 10)}T12:00:00`) : new Date();
  const prev = task.due_date || null;
  base.setDate(base.getDate() + 1);
  try { await workspaceApi.patchTask(task.id, { due_date: base.toISOString().slice(0, 10) }); }
  catch (e) { toast.error("Couldn't move due date", { description: e?.response?.data?.detail || "" }); return; }
  onChanged?.(); emitTasksChanged();
  toast("Due moved +1 day", { duration: 12000, action: { label: "Undo", onClick: async () => {
    await workspaceApi.patchTask(task.id, { due_date: prev }); onChanged?.(); emitTasksChanged();
  } } });
}

export async function claimWithUndo(task, onChanged, onUndone) {
  try { await workspaceApi.claimTask(task.id); }
  catch (e) { toast.error("Claim failed", { description: e?.response?.data?.detail || "" }); return; }
  onChanged?.(); emitTasksChanged();
  toast.success(`Claimed · ${task.title}`, { duration: 12000, action: { label: "Undo", onClick: async () => {
    await workspaceApi.patchTask(task.id, { assignee_id: null }); onUndone?.(); onChanged?.(); emitTasksChanged();
  } } });
}
